/**
 * Catálogo de audio del motor.
 *
 * Todos los archivos viven en `public/assets/audio/` y provienen de Pixabay
 * (Pixabay Content License: uso comercial libre, sin atribución obligatoria).
 * Aun así se registra autor y URL de cada pieza para poder acreditarlas y
 * para localizarlas si algún día hay que sustituirlas.
 */

import type { MusicMood } from '../types/game';

export type { MusicMood };

export type SfxName =
  | 'agua'
  | 'aplauso'
  | 'buho'
  | 'correcto'
  | 'error'
  | 'exito'
  | 'fantasma'
  | 'latido'
  | 'magia'
  | 'moneda'
  | 'monstruo'
  | 'nivel'
  | 'pato'
  | 'pop'
  | 'puerta'
  | 'risas'
  | 'robot'
  | 'silbato'
  | 'tecleo'
  | 'whoosh';

export type AmbienceName = 'bosque-noche' | 'fogata';

export interface AudioAsset {
  /** Ruta relativa a la carpeta de audio. */
  src: string;
  title: string;
  author: string;
  url: string;
}

export const AUDIO_BASE_URL = '/assets/audio/';

export const MUSIC_LIBRARY: Record<MusicMood, AudioAsset> = {
  aventura: {
    src: 'music/exploracion-chiptune.mp3',
    title: 'Exploration | Chiptune RPG Adventure Theme',
    author: 'NickPanek',
    url: 'https://pixabay.com/music/video-games-exploration-chiptune-rpg-adventure-theme-336428/'
  },
  tesoro: {
    src: 'music/tesoro-chiptune.mp3',
    title: 'Treasure Hunt | 8-Bit Chiptune Adventure Music',
    author: 'NickPanek',
    url: 'https://pixabay.com/music/video-games-treasure-hunt-8-bit-chiptune-adventure-music-357568/'
  },
  batalla: {
    src: 'music/batalla-chiptune.mp3',
    title: 'RPG Battle | Chiptune Video Game Music',
    author: 'NickPanek',
    url: 'https://pixabay.com/music/video-juegos-rpg-battle-chiptune-video-game-music-346666/'
  },
  misterio: {
    src: 'music/misterio.mp3',
    title: 'Mystery Detective Investigation Music',
    author: 'ViacheslavStarostin',
    url: 'https://pixabay.com/music/suspenso-mystery-detective-investigation-music-473843/'
  },
  mazmorra: {
    src: 'music/mazmorra.mp3',
    title: '8 Bit Dungeon',
    author: 'Kaden_Cook',
    url: 'https://pixabay.com/music/video-juegos-8-bit-dungeon-251388/'
  },
  pueblo: {
    src: 'music/pueblo-alegre.mp3',
    title: 'Happy Funny Kids',
    author: 'Music_For_Videos',
    url: 'https://pixabay.com/music/optimista-happy-funny-kids-111912/'
  },
  magia: {
    src: 'music/magia.mp3',
    title: 'Light Magic',
    author: 'Magiksolo',
    url: 'https://pixabay.com/music/titulo-principal-light-magic-163952/'
  },
  victoria: {
    src: 'music/victoria.mp3',
    title: 'Fanfare Triumph Triumphant Music',
    author: 'MFCC',
    url: 'https://pixabay.com/music/titulo-principal-fanfare-triumph-triumphant-music-357757/'
  }
};

export const SFX_LIBRARY: Record<SfxName, AudioAsset> = {
  correcto: {
    src: 'sfx/correcto.mp3',
    title: 'Correct',
    author: 'DRAGON-STUDIO',
    url: 'https://pixabay.com/sound-effects/tecnolog%C3%ADa-correct-472358/'
  },
  exito: {
    src: 'sfx/exito.mp3',
    title: 'Friendly Success Notification Ding',
    author: 'Turning_Pages',
    url: 'https://pixabay.com/sound-effects/pel%C3%ADculas-y-efectos-especiales-friendly-success-notification-ding-606325/'
  },
  error: {
    src: 'sfx/error-gracioso.mp3',
    title: 'ooopsie sound effect',
    author: 'u_v3rvrfl93f',
    url: 'https://pixabay.com/sound-effects/gente-ooopsie-sound-effect-166566/'
  },
  silbato: {
    src: 'sfx/silbato-gracioso.mp3',
    title: 'Funny Whistle Sound – Ridiculous/Goofy/Absurd',
    author: 'Senhor_A',
    url: 'https://pixabay.com/sound-effects/pel%C3%ADculas-y-efectos-especiales-funny-whistle-sound-ridiculousgoofyabsurd-356083/'
  },
  moneda: {
    src: 'sfx/moneda.mp3',
    title: 'Coin Collect (3)',
    author: 'Yodguard',
    url: 'https://pixabay.com/sound-effects/pel%C3%ADculas-y-efectos-especiales-coin-collect-3-540190/'
  },
  nivel: {
    src: 'sfx/subir-nivel.mp3',
    title: 'Level Up 05',
    author: 'Universfield',
    url: 'https://pixabay.com/sound-effects/pel%C3%ADculas-y-efectos-especiales-level-up-05-326133/'
  },
  puerta: {
    src: 'sfx/puerta-cruje.mp3',
    title: 'Door Creak',
    author: 'freesound_community',
    url: 'https://pixabay.com/sound-effects/familiar-door-creak-38423/'
  },
  fantasma: {
    src: 'sfx/fantasma.mp3',
    title: 'Spooky Cartoon Ghost Wooo',
    author: 'FadingEmbersAudio',
    url: 'https://pixabay.com/sound-effects/horror-spooky-cartoon-ghost-wooo-430234/'
  },
  monstruo: {
    src: 'sfx/monstruo.mp3',
    title: 'Deep Sea Monster Roar',
    author: 'DRAGON-STUDIO',
    url: 'https://pixabay.com/sound-effects/horror-deep-sea-monster-roar-329857/'
  },
  risas: {
    src: 'sfx/risas-ninos.mp3',
    title: 'Children Giggling Kids Laughing HD',
    author: 'Virtual_Vibes',
    url: 'https://pixabay.com/sound-effects/gente-children-giggling-kids-laughing-hd-378111/'
  },
  whoosh: {
    src: 'sfx/whoosh.mp3',
    title: 'Riser Swoosh Transition',
    author: 'DRAGON-STUDIO',
    url: 'https://pixabay.com/sound-effects/pel%C3%ADculas-y-efectos-especiales-riser-swoosh-transition-390289/'
  },
  pop: {
    src: 'sfx/pop.mp3',
    title: 'Bubble Pop',
    author: 'Universfield',
    url: 'https://pixabay.com/sound-effects/pel%C3%ADculas-y-efectos-especiales-bubble-pop-293342/'
  },
  robot: {
    src: 'sfx/robot.mp3',
    title: 'Little Robot Sound',
    author: 'freesound_community',
    url: 'https://pixabay.com/sound-effects/pel%C3%ADculas-y-efectos-especiales-little-robot-sound-84657/'
  },
  agua: {
    src: 'sfx/agua.mp3',
    title: 'Tap water splashes',
    author: '40727898',
    url: 'https://pixabay.com/sound-effects/pel%C3%ADculas-y-efectos-especiales-tap-water-splashes-176708/'
  },
  aplauso: {
    src: 'sfx/aplauso.mp3',
    title: 'Crowd Cheer and Applause',
    author: 'DRAGON-STUDIO',
    url: 'https://pixabay.com/sound-effects/gente-crowd-cheer-and-applause-406644/'
  },
  latido: {
    src: 'sfx/latido.mp3',
    title: 'Heartbeat (tense)',
    author: 'PWLPL',
    url: 'https://pixabay.com/sound-effects/pel%C3%ADculas-y-efectos-especiales-heartbeat-tense-377250/'
  },
  tecleo: {
    src: 'sfx/tecleo.mp3',
    title: 'type writing',
    author: 'freesound_community',
    url: 'https://pixabay.com/sound-effects/tecnolog%C3%ADa-type-writing-6834/'
  },
  buho: {
    src: 'sfx/buho.mp3',
    title: 'Owl Hoot',
    author: 'Universfield',
    url: 'https://pixabay.com/sound-effects/naturaleza-owl-hoot-144750/'
  },
  magia: {
    src: 'sfx/magia.mp3',
    title: 'Magic sparkle',
    author: 'Liecio',
    url: 'https://pixabay.com/sound-effects/pel%C3%ADculas-y-efectos-especiales-magic-sparkle-190030/'
  },
  pato: {
    src: 'sfx/pato.mp3',
    title: 'Duck Quack',
    author: 'iedurodrigues',
    url: 'https://pixabay.com/sound-effects/naturaleza-duck-quack-112941/'
  }
};

export const AMBIENCE_LIBRARY: Record<AmbienceName, AudioAsset> = {
  'bosque-noche': {
    src: 'ambience/bosque-noche.mp3',
    title: 'Night Forest Soundscape',
    author: '36301272',
    url: 'https://pixabay.com/sound-effects/naturaleza-night-forest-soundscape-158701/'
  },
  fogata: {
    src: 'ambience/fogata.mp3',
    title: 'Campfire Burning Wood Ambience HD',
    author: 'VirtualZero',
    url: 'https://pixabay.com/sound-effects/naturaleza-campfire-burning-wood-ambience-hd-375866/'
  }
};

/**
 * Equivalencias entre los nombres de SFX que ya usan los JSON de contenido
 * (vocabulario del generador procedural) y las piezas reales del catálogo.
 */
const LEGACY_SFX_ALIASES: Record<string, SfxName> = {
  click: 'pop',
  chime: 'exito',
  fanfare: 'nivel',
  buzz: 'error'
};

export function resolveSfxName(name: string): SfxName | null {
  if (name in SFX_LIBRARY) return name as SfxName;
  return LEGACY_SFX_ALIASES[name] ?? null;
}

export function isMusicMood(value: string): value is MusicMood {
  return value in MUSIC_LIBRARY;
}
